<script setup>
/* global defineProps */
/* eslint-disable no-unused-vars */ // Las variables de <script setup> se usan en el template
import { computed } from "vue"; // eslint-disable-line

/**
 * Grafo de los roles de una persona: quién le envía (izquierda), sus roles (centro) y a quién envía (derecha).
 * Con `otrosRoles` marca las diferencias contra el otro estado de la comparación (antes / después).
 */
const props = defineProps({
  titulo: { type: String, required: true },
  modo: { type: String, default: "despues" }, // "antes" | "despues"
  rolesPersona: { type: Array, required: true },
  otrosRoles: { type: Array, default: null },
  roles: { type: Array, required: true },
  relaciones: { type: Array, required: true },
  perfilFront: { type: Function, required: true }
});

const NODE_W = 172;
const NODE_H = 42;
const PITCH = 52;
const COL_GAP = 52;
const TOP = 30;
const COL_X = [0, NODE_W + COL_GAP, 2 * (NODE_W + COL_GAP)];
const ANCHO = 3 * NODE_W + 2 * COL_GAP;

const rolPorId = computed(() => Object.fromEntries(props.roles.map((r) => [r.id, r])));
const nombreRol = (id) => (rolPorId.value[id] ? rolPorId.value[id].name : `Rol ${id}`);
const personasRol = (id) => (rolPorId.value[id] ? rolPorId.value[id].usuarios : 0);

function tipoRol(id) {
  const p = props.perfilFront(nombreRol(id));
  if (p == "Solicitador") return "sol";
  if (p == "Verificador área") return "ver";
  if (p == "Autorizador área") return "aut";
  if (p) return "glo";
  return "otr";
}
const ordenTipo = { sol: 0, ver: 1, aut: 2, glo: 3, otr: 4 };

/** Vecinos (orígenes y destinos) de un conjunto de roles */
function vecinos(ids) {
  const set = new Set(ids);
  const entradas = props.relaciones.filter((r) => set.has(r.autorizadorId) && !set.has(r.rolId)).map((r) => r.rolId);
  const salidas = props.relaciones.filter((r) => set.has(r.rolId) && !set.has(r.autorizadorId)).map((r) => r.autorizadorId);
  return { entradas: new Set(entradas), salidas: new Set(salidas) };
}

const comparar = computed(() => props.otrosRoles != null);
const otrosSet = computed(() => new Set(props.otrosRoles || []));
const otrosVecinos = computed(() => vecinos(props.otrosRoles || []));

/** Marca de diferencia: en "después" lo nuevo se agrega, en "antes" lo que falta después se quita */
const marca = computed(() => (props.modo == "antes" ? "quita" : "agrega"));

const grafo = computed(() => {
  const centro = [...new Set(props.rolesPersona)].sort(
    (a, b) => ordenTipo[tipoRol(a)] - ordenTipo[tipoRol(b)] || nombreRol(a).localeCompare(nombreRol(b), "es")
  );
  const set = new Set(centro);
  const aristas = props.relaciones
    .filter((r) => set.has(r.rolId) || set.has(r.autorizadorId))
    .map((r) => ({ desde: r.rolId, hacia: r.autorizadorId, verificacion: r.esVerificador }));

  const pos = {};
  centro.forEach((id, i) => (pos[`c${id}`] = { id, col: 1, y: TOP + i * PITCH }));

  // Orígenes y destinos se colocan junto a los roles con los que se conectan, sin encimarse
  function colocar(ids, col, conectados) {
    const deseados = ids.map((id) => {
      const ys = conectados(id).map((c) => pos[`c${c}`].y);
      return { id, y: ys.reduce((a, b) => a + b, 0) / ys.length };
    });
    deseados.sort((a, b) => a.y - b.y);
    let ultimo = -Infinity;
    deseados.forEach((d) => {
      const y = Math.max(d.y, ultimo + PITCH, TOP);
      pos[`${col == 0 ? "i" : "d"}${d.id}`] = { id: d.id, col, y };
      ultimo = y;
    });
  }
  const origenes = [...new Set(aristas.filter((a) => set.has(a.hacia) && !set.has(a.desde)).map((a) => a.desde))];
  const destinos = [...new Set(aristas.filter((a) => set.has(a.desde) && !set.has(a.hacia)).map((a) => a.hacia))];
  colocar(origenes, 0, (id) => aristas.filter((a) => a.desde == id && set.has(a.hacia)).map((a) => a.hacia));
  colocar(destinos, 2, (id) => aristas.filter((a) => a.hacia == id && set.has(a.desde)).map((a) => a.desde));

  const nodos = Object.entries(pos).map(([clave, p]) => {
    let estado = "";
    if (comparar.value) {
      if (p.col == 1 && !otrosSet.value.has(p.id)) estado = marca.value;
      if (p.col == 0 && !otrosVecinos.value.entradas.has(p.id)) estado = marca.value;
      if (p.col == 2 && !otrosVecinos.value.salidas.has(p.id)) estado = marca.value;
    }
    return { clave, ...p, x: COL_X[p.col], tipo: tipoRol(p.id), nombre: nombreRol(p.id), personas: personasRol(p.id), estado };
  });

  const nodoDe = (id, colPreferida) => pos[`${colPreferida}${id}`] || pos[`c${id}`];
  const lineas = aristas.map((a, i) => {
    const interna = set.has(a.desde) && set.has(a.hacia);
    const d = nodoDe(a.desde, "i");
    const h = nodoDe(a.hacia, "d");
    let path;
    if (interna) {
      // Relación entre dos roles de la misma persona: arco por el lado izquierdo de la columna central
      const x = COL_X[1];
      const y1 = d.y + NODE_H / 2;
      const y2 = h.y + NODE_H / 2;
      path = `M${x},${y1} C${x - 36},${y1} ${x - 36},${y2} ${x},${y2}`;
    } else {
      const x1 = COL_X[d.col] + NODE_W;
      const x2 = COL_X[h.col];
      const y1 = d.y + NODE_H / 2;
      const y2 = h.y + NODE_H / 2;
      const mx = (x1 + x2) / 2;
      path = `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
    }
    // La relación cambia si ninguno de los roles de la persona que la forman está en el otro estado
    const extremosPersona = [a.desde, a.hacia].filter((x) => set.has(x));
    const nueva = comparar.value && extremosPersona.every((x) => !otrosSet.value.has(x));
    return { clave: `a${i}`, path, verificacion: a.verificacion, estado: nueva ? marca.value : "" };
  });

  const alto = Math.max(TOP + NODE_H, ...nodos.map((n) => n.y + NODE_H)) + 8;
  return { nodos, lineas, alto, vacio: centro.length == 0 };
});

const etiquetaEstado = computed(() => (props.modo == "antes" ? "Se quita" : "Nuevo"));
</script>
<template>
  <div class="grafo-rol">
    <div class="grafo-titulo">
      <strong>{{ titulo }}</strong>
      <span class="text-muted">{{ rolesPersona.length }} rol(es)</span>
    </div>
    <div v-if="grafo.vacio" class="grafo-vacio">Sin roles</div>
    <div v-else class="grafo-scroll">
      <div class="lienzo" :style="{ width: ANCHO + 'px', height: grafo.alto + 'px' }">
        <div class="col-head" :style="{ left: COL_X[0] + 'px' }">Le envían</div>
        <div class="col-head" :style="{ left: COL_X[1] + 'px' }">Sus roles</div>
        <div class="col-head" :style="{ left: COL_X[2] + 'px' }">Envía a</div>
        <svg class="aristas" :width="ANCHO" :height="grafo.alto">
          <path
            v-for="l in grafo.lineas"
            :key="l.clave"
            :d="l.path"
            :class="[{ verificacion: l.verificacion }, l.estado]"
          />
        </svg>
        <div
          v-for="n in grafo.nodos"
          :key="n.clave"
          class="nodo"
          :class="[n.tipo, n.estado, { central: n.col == 1 }]"
          :style="{ left: n.x + 'px', top: n.y + 'px', width: NODE_W + 'px', height: NODE_H + 'px' }"
          :title="`${n.nombre} · Rol ${n.id} · ${n.personas} persona(s)`"
        >
          <span class="nombre">{{ n.nombre }}</span>
          <span v-if="n.estado" class="marca">{{ etiquetaEstado }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import "src/sass/bootstrap-variables.scss";

.grafo-rol {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
  background: white;
  min-width: 0;
}

.grafo-titulo {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 6px;
  color: $secondary;
}

.grafo-vacio {
  color: #8b8aa6;
  padding: 24px 0;
  text-align: center;
}

.grafo-scroll {
  overflow-x: auto;
}

.lienzo {
  position: relative;
}

.col-head {
  position: absolute;
  top: 4px;
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
  color: #8b8aa6;
}

.aristas {
  position: absolute;
  inset: 0;
  overflow: visible;
  pointer-events: none;

  path {
    fill: none;
    stroke: #b9b8d3;
    stroke-width: 1.4;

    &.verificacion {
      stroke-dasharray: 5 4;
    }

    &.agrega {
      stroke: $success;
      stroke-width: 2;
    }

    &.quita {
      stroke: $danger;
      stroke-width: 2;
    }
  }
}

.nodo {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px 3px 10px;
  border: 1px solid #dddcea;
  border-radius: 7px;
  background: white;
  font-size: 11px;
  line-height: 1.2;

  &::before {
    content: "";
    position: absolute;
    left: -1px;
    top: -1px;
    bottom: -1px;
    width: 4px;
    border-radius: 7px 0 0 7px;
    background: #b9b8d3;
  }

  &.sol::before {
    background: $secondary;
  }

  &.ver::before {
    background: #0f8f8a;
  }

  &.aut::before {
    background: #c77700;
  }

  &.central {
    font-weight: 600;
    background: #f7f7fb;
  }

  &.agrega {
    border-color: $success;
    background: #e3f4ea;
  }

  &.quita {
    border-color: $danger;
    background: #fbe4e6;
    text-decoration: line-through;
    text-decoration-color: rgba(220, 53, 69, 0.5);
  }

  .nombre {
    flex: 1;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .marca {
    flex: none;
    font-size: 9.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-decoration: none;
    display: inline-block;
  }

  &.agrega .marca {
    color: $success;
  }

  &.quita .marca {
    color: $danger;
  }
}
</style>
